<script lang="ts" setup>
import {
  DocumentData,
  Query,
  Unsubscribe,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import InfiniteLoading, { type StateHandler } from "v3-infinite-loading";
import "v3-infinite-loading/lib/style.css"; //required if you're not going to override default slots
import { DocumentState } from "~/types/enums/DocumentState";
import { isCompanionSubmittedDocument } from "~/utils/submitted-document.utils";

const props = defineProps({
  states: {
    type: Array as PropType<DocumentState[]>,
    required: true,
  },
  assignmentId: {
    type: String,
    required: false,
  },
  levelId: {
    type: String,
    required: false,
  },
  lessonId: {
    type: String,
    required: false,
  },

  align: {
    type: String as PropType<"left" | "right" | "center">,
    default: "center",
  },

  filterDocuments: {
    type: Boolean,
    required: false,
    default: true,
  },
  profileId: {
    type: String,
    required: false,
  },
});

const subscription = ref<Unsubscribe | undefined>(undefined);
const pageSize = 10;
const requestedLimit = ref(pageSize);
const hasMoreDocuments = ref(false);
const isInitialLoading = ref(true);
const isLoadingMore = ref(false);
const loadError = ref(false);
let loadGeneration = 0;

const documents = ref<SubmittedDocuments>([]);

const appUserStore = useAppUserDataStore();

const sortKey = ref<
  | "lastUpdatedTimestamp"
  | "submittedAtTimestamp"
  | "createOnTimestamp"
  | undefined
>("lastUpdatedTimestamp");
const sortDirection = ref<"desc" | "asc">("desc");

const buildDocumentsQuery = (
  documentLimit: number
): Query<DocumentData> | undefined => {
  const uid = useCurrentUID();

  if (uid == undefined) return;

  const db = useFirestore();
  const documentsRef = collection(db, "documents");
  let documentsQuery = query(documentsRef, where("userId", "==", uid));

  documentsQuery = query(documentsQuery, where("state", "in", props.states));

  if (props.profileId) {
    documentsQuery = query(
      documentsQuery,
      where("profileId", "==", props.profileId)
    );
  }

  if (props.assignmentId) {
    documentsQuery = query(
      documentsQuery,
      where("assignmentId", "==", props.assignmentId)
    );
  }

  if (props.levelId) {
    documentsQuery = query(
      documentsQuery,
      where("levelId", "==", props.levelId)
    );
  }

  if (props.lessonId) {
    documentsQuery = query(
      documentsQuery,
      where("lessonId", "==", props.lessonId)
    );
  }

  return query(
    documentsQuery,
    orderBy(sortKey.value!, sortDirection.value),
    limit(documentLimit)
  );
};

const documentsFromSnapshot = (snapshot: {
  docs: Array<{ id: string; data: () => DocumentData }>;
}) =>
  snapshot.docs.map((document) => ({
    id: document.id,
    ...document.data(),
  })) as SubmittedDocuments;

const startDocumentsSubscription = ({
  documentLimit,
  state,
  initial = false,
}: {
  documentLimit: number;
  state?: StateHandler;
  initial?: boolean;
}) => {
  const documentsQuery = buildDocumentsQuery(documentLimit);

  if (!documentsQuery) {
    isInitialLoading.value = false;
    loadError.value = true;
    state?.error();
    return;
  }

  if (initial) {
    isInitialLoading.value = true;
  } else {
    isLoadingMore.value = true;
  }

  const generation = loadGeneration;
  let hasReceivedServerSnapshot = false;

  subscription.value?.();
  subscription.value = onSnapshot(
    documentsQuery,
    { includeMetadataChanges: true },
    (snapshot) => {
      if (generation !== loadGeneration) return;
      if (snapshot.metadata.fromCache) return;
      if (hasReceivedServerSnapshot && snapshot.docChanges().length === 0)
        return;

      documents.value = documentsFromSnapshot(snapshot);
      requestedLimit.value = documentLimit;
      hasMoreDocuments.value = snapshot.docs.length >= documentLimit;
      loadError.value = false;

      if (!hasReceivedServerSnapshot) {
        hasReceivedServerSnapshot = true;
        isInitialLoading.value = false;
        isLoadingMore.value = false;

        if (hasMoreDocuments.value) {
          state?.loaded();
        } else {
          state?.complete();
        }
      }
    },
    () => {
      if (generation !== loadGeneration) return;

      isInitialLoading.value = false;
      isLoadingMore.value = false;
      if (initial) {
        documents.value = [];
        loadError.value = true;
      }

      state?.error();
      useBaseToast("Unable to load documents.", "error");
    }
  );
};

const resetAndLoadDocuments = () => {
  loadGeneration += 1;
  subscription.value?.();
  subscription.value = undefined;
  documents.value = [];
  requestedLimit.value = pageSize;
  hasMoreDocuments.value = false;
  loadError.value = false;
  isInitialLoading.value = true;

  startDocumentsSubscription({ documentLimit: pageSize, initial: true });
};

const loadMoreDocuments = (state: StateHandler) => {
  if (isInitialLoading.value || isLoadingMore.value) return;

  startDocumentsSubscription({
    documentLimit: requestedLimit.value + pageSize,
    state,
  });
};

onMounted(resetAndLoadDocuments);

onUnmounted(() => {
  loadGeneration += 1;
  subscription.value?.();
});

watch(
  () =>
    JSON.stringify({
      states: [...props.states].sort(),
      profileId: props.profileId,
      assignmentId: props.assignmentId,
      levelId: props.levelId,
      lessonId: props.lessonId,
    }),
  resetAndLoadDocuments
);

const { hasIEWLMS } = storeToRefs(appUserStore);

const filteredDocuments = computed(() => {
  if (props.filterDocuments == false) {
    return documents.value;
  }

  return documents.value.filter((doc) => {
    if (isCompanionSubmittedDocument(doc)) {
      return false;
    }

    if (
      hasIEWLMS.value() != true &&
      doc.documentSubmissionType != undefined &&
      doc.documentSubmissionType != DocumentSubmissionType.finalDraft
    ) {
      return false;
    }

    return true;
  });
});

const removeDocumentFromList = (documentId: string) => {
  documents.value = documents.value.filter((doc) => doc.id !== documentId);
};

const archiveDocument = async (documentId: string) => {
  if (!documentId) return;

  await useDocuments().updateDocumentStateById(
    documentId,
    DocumentState.archived
  );
  removeDocumentFromList(documentId);
};
</script>

<template>
  <div
    v-auto-animate
    class="overflow-hidden flex flex-col pb-8 px-2 w-full pt-2"
  >
    <BaseLoading v-if="isInitialLoading" class="my-6" />
    <BaseEmptyListMessage
      v-else-if="loadError"
      class="max-w-[800px] w-full"
      :style="{
        marginLeft: align === 'right' || align == 'center' ? 'auto' : '0',
        marginRight: align === 'left' || align == 'center' ? 'auto' : '0',
      }"
    >
      Unable to load documents
    </BaseEmptyListMessage>
    <BaseEmptyListMessage
      v-else-if="filteredDocuments.length == 0"
      class="max-w-[800px] w-full"
      :style="{
        marginLeft: align === 'right' || align == 'center' ? 'auto' : '0',
        marginRight: align === 'left' || align == 'center' ? 'auto' : '0',
      }"
    >
      No Documents
    </BaseEmptyListMessage>
    <div
      v-for="document in filteredDocuments"
      :key="`${document.id}-${document.state}`"
      class="mb-2 w-full flex flex-col"
    >
      <slot :document="document" :archive-document="archiveDocument">
        <DocumentsItem
          :submitted-document="document"
          :on-archive="archiveDocument"
          class="rounded max-w-[800px] w-full border"
          :style="{
            marginLeft: align === 'right' || align == 'center' ? 'auto' : '0',
            marginRight: align === 'left' || align == 'center' ? 'auto' : '0',
          }"
        />
      </slot>
    </div>
    <InfiniteLoading
      v-if="!isInitialLoading && !loadError && hasMoreDocuments"
      class="invisible"
      :firstload="false"
      @infinite="loadMoreDocuments"
    />
  </div>
</template>

<style scoped></style>
